import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import type { Animatronic } from '../types/animatronic';
import { processMediaItems } from '../utils/mediaProcessor';

export function useAnimatronics() {
  const [animatronics, setAnimatronics] = useState<Animatronic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isConnected, setIsConnected] = useState(true);

  useEffect(() => {
    let mounted = true;

    const checkConnection = async () => {
      const connected = await supabase.checkConnection();
      if (mounted) {
        setIsConnected(connected);
      }
    };

    const fetchAnimatronics = async () => {
      try {
        if (mounted) {
          setIsLoading(true);
          setError(null);
        }

        // Check connection first
        const connected = await supabase.checkConnection();
        if (!connected) {
          throw new Error('Unable to connect to the database. Please check your internet connection.');
        }

        const result = await supabase.handleRetry(async () => {
          const { data, error: fetchError } = await supabase
            .from('animatronics')
            .select('*')
            .eq('published', true)
            .order('created_at', { ascending: false });

          if (fetchError) throw fetchError;
          return data;
        });

        if (mounted) {
          const processedAnimatronics = (result || []).map(animatronic => ({
            ...animatronic,
            media: processMediaItems(animatronic.media)
          }));

          setAnimatronics(processedAnimatronics);
          setError(null);
        }
      } catch (err) {
        console.error('Error fetching animatronics:', err);
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Failed to load animatronics'));
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    // Initial connection check
    checkConnection();

    // Set up connection monitoring
    const connectionInterval = setInterval(checkConnection, 30000);

    // Initial fetch
    fetchAnimatronics();

    // Cleanup
    return () => {
      mounted = false;
      clearInterval(connectionInterval);
    };
  }, []);

  // Extract unique values for filters
  const uniqueValues = useMemo(() => {
    const countries = new Set<string>();
    const parks = new Set<string>();
    const attractions = new Set<string>();
    const types = new Set<string>();
    const statuses = new Set<string>();
    const brands = new Set<string>();

    animatronics.forEach(animatronic => {
      if (animatronic.country) countries.add(animatronic.country);
      if (animatronic.park) parks.add(animatronic.park);
      if (animatronic.attraction) attractions.add(animatronic.attraction);
      if (animatronic.type) types.add(animatronic.type);
      if (animatronic.status) statuses.add(animatronic.status);
      if (animatronic.brand) brands.add(animatronic.brand);
    });

    return {
      uniqueCountries: Array.from(countries).sort(),
      uniqueParks: Array.from(parks).sort(),
      uniqueAttractions: Array.from(attractions).sort(),
      uniqueTypes: Array.from(types).sort(),
      uniqueStatuses: Array.from(statuses).sort(),
      uniqueBrands: Array.from(brands).sort()
    };
  }, [animatronics]);

  const retry = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await supabase.handleRetry(async () => {
        const { data, error: fetchError } = await supabase
          .from('animatronics')
          .select('*')
          .eq('published', true)
          .order('created_at', { ascending: false });

        if (fetchError) throw fetchError;
        return data;
      });

      const processedAnimatronics = (result || []).map(animatronic => ({
        ...animatronic,
        media: processMediaItems(animatronic.media)
      }));

      setAnimatronics(processedAnimatronics);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load animatronics'));
    } finally {
      setIsLoading(false);
    }
  };

  return {
    animatronics,
    isLoading,
    error,
    isConnected,
    retry,
    ...uniqueValues
  };
}